import {
  autoPlacement,
  FloatingPortal,
  flip,
  type OffsetOptions,
  offset,
  type Placement,
  shift,
  useClientPoint,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { cn } from 'cn';
import type React from 'react';
import { useState } from 'react';
import { LazyAnimatePresence, m } from '../Transitions/LazyAnimatePresence';

export interface TooltipProps {
  children: React.ReactElement;
  enabled?: boolean;
  tooltip: React.ReactNode | string;
  className?: string;
  allowedPlacements?: Placement[];
  offsetOptions?: OffsetOptions;
  id?: string;
}

export function Tooltip(props: TooltipProps) {
  const {
    children,
    tooltip,
    enabled = true,
    allowedPlacements = ['bottom-end', 'bottom-start', 'top-end', 'top-start'],
    offsetOptions = { mainAxis: -15, alignmentAxis: 5 },
  } = props;
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(offsetOptions),
      shift(),
      flip(),

      autoPlacement({
        alignment: 'start',
        allowedPlacements,
      }),
    ],
  });

  const clientPoint = useClientPoint(context);
  const hover = useHover(context);
  const focus = useFocus(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    clientPoint,
    focus,
    role,
  ]);

  if (!enabled || !tooltip) return children;

  // Filter out invalid DOM attributes from floating-ui
  const referenceProps = getReferenceProps();
  const { preventFocusOnPress, ...validReferenceProps } =
    referenceProps as typeof referenceProps & { preventFocusOnPress?: boolean };

  return (
    <>
      <div
        ref={refs.setReference}
        {...validReferenceProps}
        className={'contents'}
      >
        {children}
      </div>
      <LazyAnimatePresence>
        {isOpen ? (
          <FloatingPortal>
            <m.div
              ref={refs.setFloating}
              style={floatingStyles}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
              {...getFloatingProps()}
              id={props.id}
              data-testid="tooltip"
              className={cn(
                'typ-ui-sm w-contents pointer-events-none z-110 w-max max-w-[238px] rounded bg-neutral-200 px-1.5 py-0.5 normal-case text-white',
                'kunde:py-1 kunde:px-2 kunde:typ-ui kunde:bg-neutral-110 kunde:shadow-[0_0_8px_0_rgba(26,27,28,0.16)]',
                props.className,
              )}
            >
              {tooltip}
            </m.div>
          </FloatingPortal>
        ) : null}
      </LazyAnimatePresence>
    </>
  );
}

export default Tooltip;
